/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import { ClientApiProps, ICallsignApplicationEx, yupPhoneTestOptions } from '../../common';
import {
  AddressPicker,
  AddressSchema,
  Button,
  Checkbox,
  ErrorScope,
  FieldSet,
  FormContext,
  FormPage,
  FormPageContext,
  Label,
  PhoneInput,
  RadioList,
  RadioListOption,
  SecurePay,
  SecurePayMethods,
  Select,
  SelectOption,
  TextArea,
  TextInput,
  TokenisedCard,
  ValidationHint,
} from 'acma-spa/dist/';
import {
  Acma_amateurradiocallsignavailability,
  Acma_amateurradioassessmenttypes,
  Acma_amateurradiocallsign_statuscode,
  Acma_amateurradiocallsignprefixes,
  Acma_amateurradiocallsigntypes,
  Acma_amateurradiolocations,
  IOperatorAssessment,
  IOperatorCallsign,
  getApiClient,
  Address,
} from '../../api';
import { CallsignPicker } from '../../common/CallsignPicker';
import { AuthorisationModal } from '../../common/AuthorisationModal';
import DatePicker from 'react-multi-date-picker';
import DatePanel from 'react-multi-date-picker/plugins/date_panel';
import { PublicClientApplication } from '@azure/msal-browser';
import { Acma_amateurradioassessment_statuscode, Acma_amateurradioassessortypes, Acma_amateurradioreservationtypes, IChargingRule, IsClientIdentifierValidResult } from '../../api/client';
import { Link } from '../../common/FormHelpers';
import { PrivacyDeclaration } from './PrivacyDeclaration';

const createSchema = (baseUrl: string, b2cDomainName: string, msalClient: PublicClientApplication) => {
  const [client] = getApiClient(baseUrl, b2cDomainName, msalClient);

  return yup.object({
    id: yup.string(),
    referenceNumber: yup.string(),
    clientIdentificationNumber: yup.string().test(
      'clientid-backend-validation',
      'Invalid Client Identifier',
      async (value, context) => {
        if (value === undefined || value == null || value.length <= 0) {
          return true;
        }
        else {
          const valid = await client.validateClientidentifier(value);

          if (valid == IsClientIdentifierValidResult.InvalidClient)
            return context.createError({ message: 'The client identification number provided could not be found' });

          if (valid == IsClientIdentifierValidResult.PreferredContactNotEmail) {
            return context.createError({
              message:
                'Our records show that you have requested that the ACMA should communicate with you using mail. If you would like to submit an online application for a call sign, you can contact our ACMA Customer Service Centre who can update your preferred method of contact. Alternatively, you can download, complete and return an application form.'
            });
          }

          if (valid == IsClientIdentifierValidResult.FoundNoContactForAccount) {
            return context.createError({
              message:
                "We don't have sufficient contact information on your Client ID to progress this application.Please contact the ACMA Customer Service Centre to update your contact information on your Client ID.Alternatively, you can download, complete and return an application form."
            });
          }

          if (valid == IsClientIdentifierValidResult.FoundMultipleContactsForAccount) {
            return context.createError({
              message:
                "We don't have sufficient contact information on your Client ID to progress this application.Please contact the ACMA Customer Service Centre to update your contact information on your Client ID.Alternatively, you can download, complete and return an application form."
            });
          }

          if (valid == IsClientIdentifierValidResult.NoEmailRecorded) {
            return context.createError({
              message:
                'This Client ID does not have any email address recorded. If you would like to submit an online application for a call sign, you can contact the ACMA Customer Service Centre who can update the information on your Client ID. Alternatively you can download complete and return an application form.'
            });
          }

          return true;
        }
      }
    ),
    title: yup.string(),
    firstName: yup
      .string()
      .label('First Name')
      .when('clientIdentificationNumber',
        (clientIdentificationNumber) => clientIdentificationNumber.length == 0 ? yup.string().required() : yup.string().notRequired()
      )
    ,
    lastName: yup.string().label('Last Name')
      .when('clientIdentificationNumber',
        (clientIdentificationNumber) => clientIdentificationNumber.length == 0 ? yup.string().required() : yup.string().notRequired()
      )
    ,
    emailAddress: yup.string().email().label('Email address')
      .when('clientIdentificationNumber',
        (clientIdentificationNumber) => clientIdentificationNumber.length == 0 ? yup.string().required() : yup.string().notRequired()
      )
    ,
    phone1: yup.string().label('Contact phone').test(yupPhoneTestOptions)
      .when('clientIdentificationNumber',
        (clientIdentificationNumber) => clientIdentificationNumber.length == 0 ? yup.string().required() : yup.string().notRequired()
      )
    ,
    phone2: yup.string().label('Other phone').test(yupPhoneTestOptions),
    otherName: yup.string(),
    address1: AddressSchema.default(undefined).required('Address is required'),
    prefix: yup.mixed<Acma_amateurradiocallsignprefixes>().oneOf(Object.values(Acma_amateurradiocallsignprefixes)).required().label('Prefix'),
    location: yup
      .mixed<Acma_amateurradiolocations>()
      .oneOf(Object.values(Acma_amateurradiolocations))
      .when(['prefix'], {
        is: (prefix: Acma_amateurradiocallsignprefixes) => prefix !== Acma_amateurradiocallsignprefixes.SpecialEvent,
        then: (schema) => schema.required(),
      })
      .label('Location'),
    type: yup
      .mixed<Acma_amateurradiocallsigntypes>()
      .oneOf(Object.values(Acma_amateurradiocallsigntypes))
      .label('Type')
      .when(['prefix'], {
        is: (prefix: Acma_amateurradiocallsignprefixes) => prefix != Acma_amateurradiocallsignprefixes.SpecialEvent,
        then: (schema) => schema.required(),
      }),
    letters: yup
      .string()
      .label('Call sign')
      .when(['prefix'], {
        is: (prefix: Acma_amateurradiocallsignprefixes) => prefix === Acma_amateurradiocallsignprefixes.SpecialEvent,
        then: (schema) =>
          schema
            .required()
            .matches(/^(VK|AX|VI)[A-Z0-9]{1,14}$/gm, {
              excludeEmptyString: true,
              message: 'That call sign selected is unavailable. Please provide another call sign.',
            })
            .test({
              test: async (v?: string) => {
                if (!v) return true;

                let res = (v.match(/[0-9]/gm)?.length ?? 0) <= 4 && (v.match(/[A-Z]/gm)?.length ?? 0) <= 12;
                if (!res) return res;

                let callSigns = await client.callsignRegistryGet(undefined, undefined, undefined, undefined, v, 1);
                res = callSigns.length === 0 || callSigns[0].availability === Acma_amateurradiocallsignavailability.Available;

                if (/^AX[0-9][A-Z]{1,3}$/gm.test(v)) {
                  callSigns = await client.callsignRegistryGet(undefined, undefined, undefined, undefined, 'VK' + v.substring(2), 1);
                  res = callSigns.length === 0 || callSigns[0].availability === Acma_amateurradiocallsignavailability.Available;
                }

                return res;
              },
              message: 'That call sign selected is unavailable. Please provide another call sign.',
            }),
      }),
    specialEventDetails: yup.string().when(['prefix'], {
      is: (prefix: Acma_amateurradiocallsignprefixes) => prefix === Acma_amateurradiocallsignprefixes.SpecialEvent,
      then: (schema) => schema.required('Answer is required'),
    }),
    dates: yup
      .string()
      .label('Dates')
      .when(['prefix'], {
        is: (prefix: Acma_amateurradiocallsignprefixes) => prefix == Acma_amateurradiocallsignprefixes.SpecialEvent,
        then: (schema) => schema.required(),
      }),
    status: yup.mixed<Acma_amateurradiocallsign_statuscode>().oneOf(Object.values(Acma_amateurradiocallsign_statuscode)).optional(),
    confirmation: yup.boolean().required('Confirmation required').isTrue('Confirmation required'),
    assessmentReferenceNumber: yup.string(),
    assessment: yup.object<IOperatorAssessment>(),
    reservedTo: yup.string(),
    fileNames: yup.array().when(['location', 'reservedTo', 'assessment', 'clubRepresentative'], {
      is: (location: Acma_amateurradiolocations, reservedTo: string, assessment: IOperatorAssessment, clubRepresentative: boolean) => {
        const res =
          location == Acma_amateurradiolocations.AustralianExternalTerritories ||
          location == Acma_amateurradiolocations.Antarctic ||
          (reservedTo ? true : false) ||
          (!assessment ? true : false) ||
          clubRepresentative;
        return res;
      },
      then: (schema) => schema.defined('At least one document is required').min(1, 'At least one document is required'),
    }),
    tokenisedCard: yup.string(),
    outsideOfStateReason: yup.string().when(['location', 'address1'], {
      is: (location: Acma_amateurradiolocations, address: Address) => {
        return location && location !== address.state;
      },
      then: (schema) => schema.required('A reason is required'),
    }),
    clubRepresentative: yup.boolean().required('Confirmation required'),
    clubName: yup
      .string()
      .label('Club name')
      .when(['clubRepresentative'], {
        is: (clubRepresentative?: boolean) => clubRepresentative == true,
        then: (schema) => schema.required(),
      }),
    availability: yup.mixed<Acma_amateurradiocallsignavailability>().oneOf(Object.values(Acma_amateurradiocallsignavailability)).optional(),
    confirmationRepresentingOrganisation: yup.boolean().when(['availability'], {
      is: (availability: Acma_amateurradiocallsignavailability) => availability === Acma_amateurradiocallsignavailability.Reserved,
      then: (schema) => schema.required('Confirmation required'),
    }),
  });
};

export const getOperatorAssessment = (assessmentList: IOperatorAssessment[] | undefined) => {
  assessmentList = (assessmentList ?? []).sort((a, b) => (a.createdOn! > b.createdOn! ? 1 : a.createdOn! < b.createdOn! ? -1 : 0));
  assessmentList = assessmentList.filter(
    (r) =>
      r.status === Acma_amateurradioassessment_statuscode.Issued ||
      r.status === Acma_amateurradioassessment_statuscode.Requested ||
      r.status === Acma_amateurradioassessment_statuscode.Approved,
  );
  let assessment = assessmentList.filter((r) => r.type == Acma_amateurradioassessmenttypes.Advanced);

  if (assessment.length == 0) assessment = assessmentList.filter((r) => r.type == Acma_amateurradioassessmenttypes.Standard);

  if (assessment.length == 0) assessment = assessmentList.filter((r) => r.type == Acma_amateurradioassessmenttypes.Foundation);

  return assessment.length > 0 ? assessment[0] : undefined;
};

export interface CallsignApplicationPageProps extends ClientApiProps {
  ctx: FormContext<ICallsignApplicationEx>;
  onFileAdded: (files: File[]) => string[];
  onFileRemoved: (name: string) => string[];
  callsigns: IOperatorCallsign[];
  chargingRules: IChargingRule[];
  onCallsigns: (callsigns: IOperatorCallsign[]) => any;
}

export const getLocationOptions = () => {
  const options: SelectOption<Acma_amateurradiolocations>[] = [];
  for (const enumMember in Acma_amateurradiolocations) {
    if (enumMember != Acma_amateurradiolocations.Antarctic) {
      options.push({
        value: enumMember as Acma_amateurradiolocations,
        label: enumMember == Acma_amateurradiolocations.AustralianExternalTerritories ? 'Australian external territories' : enumMember,
      });
    }
  }

  options.push({
    value: Acma_amateurradiolocations.Antarctic,
    label: 'Antarctic',
  });

  return options;
};

export const getTypeOptions = (prefix: Acma_amateurradiocallsignprefixes | undefined, noDescription?: boolean) => {
  let options: RadioListOption<Acma_amateurradiocallsigntypes>[] = [];

  if (prefix == Acma_amateurradiocallsignprefixes.VK) {
    options = [
      {
        value: Acma_amateurradiocallsigntypes.ThreeLetter,
        label: '3 letter call sign' + (noDescription ? '' : ' - For amateurs of any level (Foundation, Standard and Advanced)'),
      },
      {
        value: Acma_amateurradiocallsigntypes.TwoLetter,
        label: '2 letter call sign' + (noDescription ? '' : ' - For Advanced amateurs only'),
      },
      {
        value: Acma_amateurradiocallsigntypes.TwoByOneContest,
        label: '2 x 1 Contest' + (noDescription ? '' : ' call signs - For Advanced amateurs during amateur radio contests only'),
      },
    ];
  }

  return options;
};

const yesNoOptions: RadioListOption<boolean>[] = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

const prefixOptions: RadioListOption<Acma_amateurradiocallsignprefixes | undefined>[] = [
  { value: Acma_amateurradiocallsignprefixes.VK, label: 'VK – For amateurs of any level (Foundation, Standard and Advanced) with a few exceptions' },
  {
    value: Acma_amateurradiocallsignprefixes.VJ,
    label: 'VJ – Advanced level only for contests (not for general amateur radio use)',
  },
  {
    value: Acma_amateurradiocallsignprefixes.VL,
    label: 'VL – Advanced level only for contests (not for general amateur radio use)',
  },
  {
    value: Acma_amateurradiocallsignprefixes.SpecialEvent,
    label: 'AX, VI or VK – Special event call sign for local, national or international occasions',
  },
];

function GetFullPrefix(prefix: Acma_amateurradiocallsignprefixes | undefined, location: Acma_amateurradiolocations | undefined) {
  if (!prefix || !location) return undefined;

  let locationId = 0;
  for (const enumMember in Acma_amateurradiolocations) {
    if (enumMember == location) break;
    ++locationId;
  }

  return `${prefix}${locationId}`;
}

export const CallsignApplicationPage: React.FC<CallsignApplicationPageProps> = ({ ctx, ...props }) => {
  const securePayRef = useRef<SecurePayMethods>(null);

  return (
    <FormPage<ICallsignApplicationEx>
      ctx={ctx}
      schema={createSchema(props.baseUrl, props.b2cDomainName, props.msalClient) as yup.ObjectSchema<ICallsignApplicationEx>}
      onNext={async () => {
        if (securePayRef.current != null) {
          securePayRef.current.tokenise();
        }
        return false;
      }}>
      {(ctx: FormPageContext<ICallsignApplicationEx>) => {
        /**
         * Note: React expects "Updater Functions" to update useState hooks.
         * However, FormPage does not allow this when calling setModel(). We can only pass a raw value.
         * eg: setModel(x+1) vs setModel(x => x+1)
         * @see: https://react.dev/reference/react/useState
         */
        const { model, setModel, errors } = ctx;

        const [authorisation, setAuthorisation] = useState(false);
        const [tokenisedCard, setTokenisedCard] = useState<TokenisedCard | null>(null);
        const [dates, setDates] = useState<string[]>(() => model.dates?.split(',').map((r) => r.trim()) ?? []);

        const typeOptions = getTypeOptions(model.prefix);
        const fullPrefix = GetFullPrefix(model.prefix, model.location);

        let callsignIndex: number | null = props.callsigns.findIndex(
          (r) => r.name == `${GetFullPrefix(Acma_amateurradiocallsignprefixes.VK, model.location)}${model.letters}`,
        );
        if (callsignIndex < 0) callsignIndex = null;
        const isSpecialLocation =
          model.location == Acma_amateurradiolocations.Antarctic || model.location == Acma_amateurradiolocations.AustralianExternalTerritories;
        const isAdvanced =
          model.prefix == Acma_amateurradiocallsignprefixes.SpecialEvent
            ? false
            : (model.prefix && model.prefix != Acma_amateurradiocallsignprefixes.VK) ||
            (model.type && model.type != Acma_amateurradiocallsigntypes.ThreeLetter);

        let assessment = isAdvanced ? (model.assessment?.type == Acma_amateurradioassessmenttypes.Advanced ? model.assessment : undefined) : model.assessment;
        assessment =
          model.assessment?.status === Acma_amateurradioassessment_statuscode.Issued ||
            model.assessment?.status === Acma_amateurradioassessment_statuscode.Requested ||
            model.assessment?.status === Acma_amateurradioassessment_statuscode.Approved
            ? assessment
            : undefined;
        const representingAClub = (model.clubRepresentative || false) && model.clubRepresentative === true;
        useEffect(() => {
          setModel({ assessmentReferenceNumber: assessment?.referenceNumber }, ValidationHint.NoValidation);
        }, [assessment]);

        const [reservedMsg, setReservedMsg] = useState(false);
        const onSpecialReservation = async (search: string) => {
          if (!search) return;
          const [client] = getApiClient(props.baseUrl, props.b2cDomainName, props.msalClient);
          let callSigns = await client.callsignRegistryGet(undefined, undefined, undefined, undefined, search, 1);
          setReservedMsg(callSigns.length > 0 && callSigns[0].reservationType == Acma_amateurradioreservationtypes.SpecialEvent);
        };

        useEffect(() => {
          if (!model.letters) setReservedMsg(false);
          else onSpecialReservation(model.letters);
        }, [model.letters]);

        const onAuthorisation = async (validated: boolean) => {
          setAuthorisation(false);
          if (validated) {
            const [client] = getApiClient(props.baseUrl, props.b2cDomainName, props.msalClient);
            const operator = await client.operatorGet(undefined);
            setModel({ assessment: getOperatorAssessment(operator.assessments) }, ValidationHint.NoValidation);
            props.onCallsigns(operator?.callsigns ?? []);
          }
        };

        const [showContactFields, setShowContactFields] = useState(true);

        useEffect(() => {
          if (model.clientIdentificationNumber)
            setShowContactFields((false));
          else
            setShowContactFields(true);
        }, [model.clientIdentificationNumber]);

        const getChargingRule = (model: ICallsignApplicationEx, pickNextAvailable?: boolean): IChargingRule => {
          pickNextAvailable = pickNextAvailable == undefined ? (model.letters ? false : true) : pickNextAvailable;

          for (let i = 0; i < props.chargingRules.length; i++) {
            const chargingRule = props.chargingRules[i];

            const prefixMatched = chargingRule.prefixes?.length === 0 || chargingRule.prefixes?.some((pfx) => pfx === model.prefix);
            const locationMatched = chargingRule.locations?.length === 0 || chargingRule.locations?.some((loc) => loc === model.location);
            const typeMatched = chargingRule.types?.length === 0 || chargingRule.types?.some((typ) => typ === model.type);
            const nextAvailMatched = chargingRule.pickedNextAvailable === undefined || chargingRule.pickedNextAvailable === pickNextAvailable;

            if (prefixMatched && locationMatched && typeMatched && nextAvailMatched) {
              return chargingRule;
            }
          }

          return props.chargingRules[props.chargingRules.length - 1]; //returning default
        };

        useEffect(() => {
          if (!tokenisedCard) {
            return;
          }
          ctx.onNavigate({ ...model, tokenisedCard: tokenisedCard.token }, true, true);
        }, [tokenisedCard]);

        const chargingRuleForNextAvailable = getChargingRule(model, true);
        const chargingRuleForPreffered = getChargingRule(model, false);

        return (
          <>
            <p>Please complete this form and supply supporting documents, where applicable if you would like to apply to be assigned a call sign.</p>
            <p>
              Your application will be processed in accordance with the{' '}
              {Link(props.contentUrls.examRules, 'Radiocommunications (Amateur Stations) Class Licence 2023')} and the ACMA&apos;s{' '}
              {Link(props.contentUrls.callSignPolicy, 'call sign policy')}.
            </p>
            <p>
              Once you submit this form you will receive an email from the ACMA confirming that your application has been received and attaching your receipt.
            </p>

            <div className="mb-3">
              <FieldSet label="Choose call sign prefix" error={errors?.prefix} required={true}>
                <RadioList<Acma_amateurradiocallsignprefixes | undefined>
                  id="prefix"
                  disabled={ctx.readOnly}
                  options={prefixOptions}
                  value={model.prefix}
                  onChange={(v) =>
                    setModel(
                      {
                        prefix: v,
                        type: v == Acma_amateurradiocallsignprefixes.VK ? undefined : Acma_amateurradiocallsigntypes.TwoByOneContest,
                        letters: undefined,
                        reservedTo: undefined,
                        location: undefined,
                      },
                      ['prefix'],
                    )
                  }
                />
              </FieldSet>
            </div>

            {typeOptions.length > 0 ? (
              <div className="mb-3">
                <FieldSet label="Choose call sign type" error={errors?.type} required={true}>
                  <RadioList<Acma_amateurradiocallsigntypes | undefined>
                    id="type"
                    disabled={ctx.readOnly}
                    options={typeOptions}
                    value={model.type}
                    size='block'
                    onChange={(v) =>
                      setModel(
                        {
                          type: v,
                          letters: undefined,
                          reservedTo: undefined,
                        },
                        ['type'],
                      )
                    }
                  />
                </FieldSet>
              </div>
            ) : (
              <></>
            )}

            {model.prefix && model.prefix != Acma_amateurradiocallsignprefixes.SpecialEvent ? (
              <div className="row">
                <div className="col-sm-4 mb-3">
                  <Label id="location" label="Location where you will use call sign" error={errors?.location} required={true}>
                    <Select<Acma_amateurradiolocations>
                      disabled={ctx.readOnly}
                      options={getLocationOptions()}
                      value={model.location || null}
                      defaultLabel="Select..."
                      size="block"
                      onChange={(type) =>
                        setModel({
                          location: type || undefined,
                          letters: undefined,
                          reservedTo: undefined,
                        })
                      }
                    />
                  </Label>
                </div>
              </div>
            ) : (
              <></>
            )}
            {model.prefix != Acma_amateurradiocallsignprefixes.SpecialEvent && fullPrefix && model.type && (
              <div className="row">
                <div className="col-sm-12 mb-3">
                  <Label
                    id="letters"
                    label="Call sign"
                    error={errors?.letters}
                    hint={
                      <>
                        {chargingRuleForNextAvailable.price != chargingRuleForPreffered.price && (
                          <p>
                            <b>Note:</b> By selecting a specific callsign, you will be increasing the price from ${chargingRuleForNextAvailable.price} to $
                            {chargingRuleForPreffered.price} AUD.
                          </p>
                        )}
                        {model.type === Acma_amateurradiocallsigntypes.TwoByOneContest && (
                          <p>
                            <b>Note:</b> Please note - 2 x 1 contest call signs are usually assigned for a period of 12 months.
                          </p>
                        )}
                        {model.prefix === Acma_amateurradiocallsignprefixes.VK &&
                          (model.location === Acma_amateurradiolocations.Antarctic ||
                            model.location === Acma_amateurradiolocations.AustralianExternalTerritories) && (
                            <p>
                              <b>Note:</b> Please note - this type of call sign is usually assigned for a period of 12 months.
                            </p>
                          )}
                      </>
                    }>
                    <CallsignPicker
                      {...props}
                      disabled={ctx.readOnly}
                      value={model.letters ? { name: model.letters, reservedTo: model.reservedTo } : null}
                      onChange={(v) => {
                        setModel({ letters: v?.name || undefined, reservedTo: v?.reservedTo });
                      }}
                      type={model.type}
                      prefix={fullPrefix}
                    />
                  </Label>
                </div>
              </div>
            )}

            {model.prefix == Acma_amateurradiocallsignprefixes.SpecialEvent ? (
              <>

                {reservedMsg && (
                  <div className="row">
                    <div className="col-sm-12 mb-2">
                      <div className="alert alert-info" role="alert">
                        Please note, the call sign selected is reserved for use during the declared United Nations International Years.
                      </div>
                    </div>
                  </div>
                )}

                <div className="row">
                  <div className="col-sm-12 mb-2">
                    <div className="alert alert-info" role="alert">
                      Class-licensed amateurs can swap their assigned VK 2 or 3 letter call sign to AX on Australia Day, ANZAC Day and World Telecommunications Day without applying.
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-sm-4 mb-3">
                    <Label id="letters" label="Enter call sign" error={errors?.letters} required>
                      <TextInput
                        readonly={ctx.readOnly}
                        value={model.letters || ''}
                        onChange={async (v) => {
                          setModel({ letters: v?.toUpperCase() ?? undefined });
                          onSpecialReservation(v);
                        }}
                        maxLength={16}
                      />
                    </Label>
                  </div>
                  <div className="col-sm-8 mb-3">
                    <Label
                      id="dates"
                      label={`Choose date/s of local, national or international day of significance you plan to use this call sign`}
                      required
                      error={errors?.dates}>
                      <DatePicker
                        multiple
                        inputClass="amateurradio__au-text-input amateurradio__au-text-input--block mt-2"
                        containerStyle={{ width: '100%' }}
                        format="DD/MM/YYYY"
                        value={dates}
                        plugins={[<DatePanel key={1} />]}
                        onChange={(v) => {
                          if (Array.isArray(v)) {
                            const dateStrings: string[] = v.map((r) => r.toString());
                            const dates = dateStrings.join(', ');
                            console.log(dates);
                            setModel({ dates });
                            setDates(dateStrings);
                          }
                        }}
                      />
                    </Label>
                  </div>
                </div>

                <div className="row">
                  <div className="col-sm-12 mb-3">
                    <Label
                      id="specialEventDetails"
                      label={
                        <>
                          If you want to continue with the application, please outline the event that you are applying to use this special event call sign on in
                          the box below. Please also indicate how the proposed special event call sign relates to the date of national/international
                          significance. This application will be assessed against the ACMA call sign policy.
                        </>
                      }
                      error={errors?.specialEventDetails}
                      required>
                      <TextArea
                        readonly={ctx.readOnly}
                        value={model.specialEventDetails || ''}
                        onChange={(v) => setModel({ specialEventDetails: v })}
                        maxLength={400}
                        rows={5}
                      />
                    </Label>
                  </div>
                </div>
              </>
            ) : (
              <></>
            )}
            {model.location && model.location !== model.address1?.state && (
              <>
                {(
                  <div className="alert alert-info" role="alert">
                    <div className="row">
                      <div className="col-sm-12 mb-2">
                        <Label
                          id="assessmentReferenceNumber"
                          label={
                            <>
                              Our system indicates that you are applying for a call sign outside your state or territory of residence.
                              If this is incorrect or not current, you can update your contact details in ACMA Assist or by contacting
                              the Customer Service Centre on 1300 850 115. If you wish to continue, please outline the reason you are
                              applying for a call sign outside your state or territory of residence.
                              Please note, your application will be assessed consistent with the ACMA {Link(props.contentUrls.callSignPolicy, 'call sign policy')}.
                            </>
                          }
                          error={errors?.outsideOfStateReason}>
                          <TextArea
                            readonly={ctx.readOnly}
                            value={model.outsideOfStateReason || ''}
                            onChange={(v) => setModel({ outsideOfStateReason: v })}
                            maxLength={400}
                            rows={5}
                          />
                        </Label>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
            <div className="row">
              <div className="col-sm-4 mb-3">
                <FieldSet label="Are you applying on behalf of a club?" error={errors?.clubRepresentative} required={true}>
                  <RadioList<boolean | undefined>
                    id="clubRepresentative"
                    disabled={ctx.readOnly}
                    options={yesNoOptions}
                    value={model.clubRepresentative}
                    onChange={(v) => setModel({ clubRepresentative: v })}
                  />
                </FieldSet>
              </div>
              {model.clubRepresentative && (
                <div className="col-sm-8 mb-3">
                  <Label id="clubName" label="Name of the club you are applying on behalf of" error={errors?.clubName} required={true}>
                    <TextInput readonly={ctx.readOnly} value={model.clubName || ''} onChange={(v) => setModel({ clubName: v })} maxLength={400} />
                  </Label>
                </div>
              )}
            </div>

            {!assessment && (
              <div className="alert alert-info" role="alert">
                <h4>We could not find record of {isAdvanced ? 'Advanced' : 'any'} qualification.</h4>
                <p>If you used a different email address in the past, please try locating your qualifation records using that email address.</p>
                <Button label="Find qualification records" id="find-qualification-records" onClick={() => setAuthorisation(true)} />
                <p className="mt-3">Alternatively, please provide your existing certificate number and/or upload supporting documentation below.</p>
                <div className="row">
                  <div className="col-sm-4 mb-3">
                    <Label id="assessmentReferenceNumber" label="Call sign / Certificate number" error={errors?.assessmentReferenceNumber}>
                      <TextInput
                        readonly={ctx.readOnly}
                        value={model.assessmentReferenceNumber || ''}
                        onChange={(v) => setModel({ assessmentReferenceNumber: v })}
                        maxLength={400}
                      />
                    </Label>
                  </div>
                </div>
              </div>
            )}

            {(!assessment || isSpecialLocation || (model.reservedTo ? true : false) || representingAClub) && (
              <>
                <h2 className="mb-2">
                  Supporting documents
                  <span style={{ color: 'red', display: 'inline' }} aria-hidden="true">
                    *
                  </span>
                </h2>
                {isSpecialLocation && (
                  <p>
                    Please provide evidence of your residency or your intention to travel to the{' '}
                    {model.location === Acma_amateurradiolocations.Antarctic ? 'Antarctic' : 'Australian external territory'}. For further information about
                    what evidence would be acceptable for this purpose please refer to the ACMA's {Link(props.contentUrls.callSignPolicy, 'call sign policy')}.
                  </p>
                )}
                {representingAClub && (
                  <p>
                    You have applied for a call sign on behalf of a club. In your supporting documents please include evidence from the club you are
                    representing that the club is considered to be a separate legal entity. Further information about the type of evidence required can be found
                    in the ACMA's {Link(props.contentUrls.callSignPolicy, 'call sign policy')}.
                  </p>
                )}
                {model.reservedTo && (
                  <p>
                    You have applied for a call sign reserved to a particular group. Please provide evidence that you have authority to apply for this call sign
                    on behalf of this group. For further information about what evidence would be acceptable for this purpose please refer to the ACMA’s{' '}
                    {Link(props.contentUrls.callSignPolicy, 'call sign policy')}.
                  </p>
                )}
                <p>
                  {representingAClub && 'You must hold an Advanced qualification to apply for a call sign on behalf of a club. '}
                  {!representingAClub && model.reservedTo && 'You must hold an Advanced qualification to apply for this type of call sign. '}
                  {!representingAClub &&
                    !model.reservedTo &&
                    !assessment &&
                    isAdvanced &&
                    'You must hold an Advanced qualification to apply for this type of call sign. We do not hold information that indicates that you hold an Advanced Qualification. '}
                  {(assessment && !representingAClub && !model.reservedTo && (!isAdvanced || (isAdvanced && isSpecialLocation)) && <></>) ||
                    'Please include either of the following documents to assist us in assessing your application: '}
                </p>
                <ol>
                  {(assessment && !representingAClub && !model.reservedTo && (!isAdvanced || (isAdvanced && isSpecialLocation)) && <></>) || (
                    <>
                      <li>A copy of the letter or email from the ACMA sent during January 2024, confirming your amateur operator qualification level</li>
                      <li>A copy of your most recently awarded amateur radio qualification</li>
                    </>
                  )}
                </ol>

                <ErrorScope id="fileNames" error={errors.fileNames}>
                  {model.fileNames && model.fileNames.length > 0 && (
                    <Label id="files" label="Uploaded Files">
                      <ul className="list-unstyled mt-2">
                        {model.fileNames.map((r, i) => (
                          <li key={i}>
                            {r}{' '}
                            <a
                              href="#"
                              title="Remove"
                              style={{ display: ctx.readOnly ? 'none' : '' }}
                              onClick={(e) => {
                                e.preventDefault();
                                const fileNames = props.onFileRemoved(r);
                                setModel({ fileNames });
                              }}>
                              X
                            </a>
                          </li>
                        ))}
                      </ul>
                    </Label>
                  )}

                  {ctx.readOnly === false && (
                    <Button
                      id="fileNames"
                      label="Upload document"
                      className="mb-3"
                      onClick={() => document.getElementById('callsign-document-upload')?.click()}
                    />
                  )}
                </ErrorScope>

                <input
                  type="file"
                  id="callsign-document-upload"
                  style={{ display: 'none' }}
                  multiple={true}
                  accept={'.docx,.pdf,.xlsx,.png,.jpeg,.jpg,.gif'}
                  onChange={(e) => {
                    const fileNames = props.onFileAdded(e.target.files ? Array.from(e.target.files) : []);
                    setModel({ fileNames });
                    e.target.value = '';
                  }}
                />
              </>
            )}

            <h2 className="my-2">Contact details</h2>

            <div className="row">
              <div className="col-sm-4 mb-3">
                <Label id="clientIdentificationNumber" label="Existing ACMA client identification number" error={errors?.clientIdentificationNumber}>
                  <TextInput readonly={ctx.readOnly} value={model.clientIdentificationNumber || ''} onChange={(v) => setModel({ clientIdentificationNumber: v })} maxLength={10} />
                </Label>
              </div>
            </div>

            {showContactFields &&
              <div>
                <div className="row">
                  <div className="col-sm-4 mb-3">
                    <Label id="title" label="Title" error={errors?.title}>
                      <TextInput readonly={ctx.readOnly} value={model.title || ''} onChange={(v) => setModel({ title: v })} maxLength={400} />
                    </Label>
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-4 mb-3">
                    <Label id="firstName" label="First Name" error={errors?.firstName} required={true}>
                      <TextInput readonly={ctx.readOnly} value={model.firstName || ''} onChange={(v) => setModel({ firstName: v })} maxLength={149} />
                    </Label>
                  </div>
                  <div className="col-sm-4 mb-3">
                    <Label id="lastName" label="Last Name" error={errors?.lastName} required={true}>
                      <TextInput readonly={ctx.readOnly} value={model.lastName || ''} onChange={(v) => setModel({ lastName: v })} maxLength={149} />
                    </Label>
                  </div>
                  <div className="col-sm-4 mb-3">
                    <Label id="otherName" label="Other Name" error={errors?.otherName}>
                      <TextInput readonly={ctx.readOnly} value={model.otherName || ''} onChange={(v) => setModel({ otherName: v })} maxLength={149} />
                    </Label>
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-4 mb-3">
                    <Label id="emailAddress" label="Email address" error={errors?.emailAddress} required={true}>
                      <TextInput readonly={ctx.readOnly} value={model.emailAddress || ''} onChange={(v) => setModel({ emailAddress: v })} maxLength={400} />
                    </Label>
                  </div>
                  <div className="col-sm-4 mb-3">
                    <Label id="phone1" label="Contact phone" error={errors?.phone1} required={true}>
                      <PhoneInput readonly={ctx.readOnly} value={model.phone1 || ''} onChange={(v) => setModel({ phone1: v })} maxLength={100} />
                    </Label>
                  </div>
                  <div className="col-sm-4 mb-3">
                    <Label id="phone2" label="Other phone" error={errors?.phone2}>
                      <PhoneInput readonly={ctx.readOnly} value={model.phone2 || ''} onChange={(v) => setModel({ phone2: v })} maxLength={100} />
                    </Label>
                  </div>
                </div>
                <div className="row">
                  <div className="col mb-3">
                    <Label id="address1" label="Contact address" error={errors?.address1} required={true}>
                      <AddressPicker value={model.address1 || null} onChange={(address) => setModel({ address1: address || undefined })} disabled={ctx.readOnly} />
                    </Label>
                  </div>
                </div>
              </div>
            }

            {!ctx.readOnly ? (
              <>
                <h2 className="my-2">Payment details</h2>
                <h4>Total: ${getChargingRule(model).price} AUD</h4>
                <SecurePay
                  clientId={props.securePayClientId ?? ''}
                  merchantCode={props.securePayMerchantCode ?? ''}
                  uri={`${props.securePaySrc}`}
                  onLoadComplete={() => { }}
                  onTokeniseError={() => ctx.onLoading(null)}
                  onTokeniseSuccess={(v) => {
                    ctx.onLoading(null);
                    setTokenisedCard(v);
                  }}
                  ref={securePayRef}
                />
              </>
            ) : (
              <></>
            )}

            <hr />

            <div className="row">
              <div className="col mb-3">
                <ErrorScope id="confirmation" error={errors?.confirmation}>
                  <Checkbox
                    id="confirmation"
                    label="I confirm that the information provided is true and correct."
                    checked={ctx.readOnly ? true : model.confirmation ?? false}
                    onChange={(v) => setModel({ confirmation: v })}
                    disabled={ctx.readOnly}
                    required
                  />
                </ErrorScope>
              </div>
            </div>
            {model.availability && model.availability === Acma_amateurradiocallsignavailability.Reserved && (
              <div className="row">
                <div className="col mb-3">
                  <ErrorScope id="confirmationRepresentingOrganisation" error={errors?.confirmationRepresentingOrganisation}>
                    <Checkbox
                      id="confirmationRepresentingOrganisation"
                      label={
                        <>
                          I understand that I am applying for a call sign on behalf of an organisation and that my application will be assessed in accordance
                          with the ACMA’s call sign policy {Link(props.contentUrls.callSignPolicy, 'call sign policy')}.
                        </>
                      }
                      checked={ctx.readOnly ? true : model.confirmationRepresentingOrganisation ?? false}
                      onChange={(v) => setModel({ confirmationRepresentingOrganisation: v })}
                      disabled={ctx.readOnly}
                      required
                    />
                  </ErrorScope>
                </div>
              </div>
            )}

            <PrivacyDeclaration {...props} />

            <AuthorisationModal {...props} open={authorisation} onClose={onAuthorisation} />
          </>
        );
      }}
    </FormPage>
  );
};
